import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaSave, FaBell } from 'react-icons/fa';

const JobAlertModal = ({ onClose, onCreate, isDarkMode }) => {
  const [title, setTitle] = useState('');
  const [company, setCompany] = useState('');
  const [location, setLocation] = useState('');
  const [alertFrequency, setAlertFrequency] = useState('daily');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await onCreate({
        title,
        company,
        location,
        min_salary: 100,
        alert_frequency: alertFrequency,
        is_active: isActive,
      });
      onClose(); // Close modal on success
    } catch (err) {
      setError('Failed to create job alert. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const c = isDarkMode ? {
    card: 'bg-[#1C1C1C]',
    text: 'text-gray-100',
    secondaryText: 'text-gray-400',
    border: 'border-gray-700',
    input: 'bg-[#1C1C1C] border-gray-600 text-white focus:ring-gray-400 focus:border-gray-400',
    button: 'bg-gray-700 text-white hover:bg-gray-600',
  } : {
    card: 'bg-white',
    text: 'text-gray-900',
    secondaryText: 'text-gray-600',
    border: 'border-gray-300',
    input: 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-black focus:border-black',
    button: 'bg-gray-200 text-gray-800 hover:bg-gray-300',
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 50, opacity: 0 }}
        className={`${c.card} ${c.border} rounded-xl shadow-lg p-6 w-full max-w-lg mx-auto relative`}
        onClick={e => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-3 right-3 text-2xl text-gray-400 hover:text-gray-600 transition-colors">
          <FaTimes />
        </button>
        <div className="flex items-center gap-3 mb-6">
          <FaBell className={`text-3xl ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          <h2 className={`text-2xl font-bold ${c.text}`}>Create a Job Alert</h2>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${c.text}`}>Job Title</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required
              className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all ${c.input}`}
              placeholder="e.g., React Developer"
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${c.text}`}>Company</label>
            <input type="text" value={company} onChange={(e) => setCompany(e.target.value)}
              className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all ${c.input}`}
              placeholder="e.g., Google"
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${c.text}`}>Location</label>
            <input type="text" value={location} onChange={(e) => setLocation(e.target.value)}
              className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all ${c.input}`}
              placeholder="e.g., Bangalore, Hyderabad"
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${c.text}`}>Alert Frequency</label>
            <select value={alertFrequency} onChange={(e) => setAlertFrequency(e.target.value)}
              className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all ${c.input}`}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="isActive" checked={isActive} onChange={(e) => setIsActive(e.target.checked)}
              className={`w-4 h-4 rounded-lg focus:ring-2 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-200 border-gray-300'}`}
            />
            <label htmlFor="isActive" className={`${c.text}`}>Active</label>
          </div>
          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
          <motion.button
            type="submit"
            disabled={loading || !title.trim()}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`w-full px-4 py-2 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2
              ${loading ? 'opacity-70 cursor-not-allowed' : c.button}`}
          >
            <FaSave /> {loading ? 'Saving...' : 'Save Alert'}
          </motion.button>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default JobAlertModal;